import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import {
  chartColors,
  gridProps,
  axisTick,
  quietAxisProps,
  tooltipContentStyle,
  barRadius,
  MAX_BAR_SIZE,
  SERIES_FILL_OPACITY,
  activeBarProps,
} from './chartTheme';

interface CategoryBucket {
  category: string;
  amount: number;
}

interface Props {
  totalIncome: number;
  expensesByCategory: CategoryBucket[];
  nonRevenueCosts: number;
}

interface WaterfallRow {
  label: string;
  base: number; // invisible spacer to position the visible segment
  value: number; // signed visible amount
  display: number;
  kind: 'start' | 'positive' | 'negative' | 'end';
}

const CashflowWaterfall: React.FC<Props> = ({ totalIncome, expensesByCategory, nonRevenueCosts }) => {
  const { theme } = useTheme();

  const rows = useMemo<WaterfallRow[]>(() => {
    let running = 0;
    const result: WaterfallRow[] = [];

    // Start
    result.push({ label: 'Start', base: 0, value: 0, display: 0, kind: 'start' });

    // Income
    result.push({ label: 'Income', base: running, value: totalIncome, display: totalIncome, kind: 'positive' });
    running += totalIncome;

    // Expense categories (top 6)
    const sorted = [...expensesByCategory].sort((a, b) => b.amount - a.amount).slice(0, 6);
    const remainder = expensesByCategory.slice(6).reduce((s, x) => s + (x.amount || 0), 0);
    sorted.forEach((c) => {
      const v = c.amount || 0;
      result.push({
        label: c.category,
        base: running - v,
        value: v,
        display: -v,
        kind: 'negative',
      });
      running -= v;
    });
    if (remainder > 0) {
      result.push({
        label: 'Other expenses',
        base: running - remainder,
        value: remainder,
        display: -remainder,
        kind: 'negative',
      });
      running -= remainder;
    }

    // Non-revenue costs
    if (nonRevenueCosts > 0) {
      result.push({
        label: 'Non-revenue',
        base: running - nonRevenueCosts,
        value: nonRevenueCosts,
        display: -nonRevenueCosts,
        kind: 'negative',
      });
      running -= nonRevenueCosts;
    }

    // End
    result.push({ label: 'Net', base: 0, value: running, display: running, kind: 'end' });

    return result;
  }, [totalIncome, expensesByCategory, nonRevenueCosts]);

  if (totalIncome === 0 && expensesByCategory.length === 0) {
    return (
      <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.gray?.[400] }}>
        Nothing to plot for this range
      </Box>
    );
  }

  const colorFor = (kind: WaterfallRow['kind'], value: number) => {
    if (kind === 'start' || kind === 'end') {
      return value >= 0 ? chartColors.income : chartColors.expense;
    }
    if (kind === 'positive') return chartColors.income;
    return chartColors.expense;
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={rows} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="label" tick={axisTick} interval={0} angle={-15} textAnchor="end" height={60} {...quietAxisProps} />
        <YAxis
          tick={axisTick}
          tickFormatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
          {...quietAxisProps}
        />
        <Tooltip
          formatter={(_: number, _name: string, item) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const p = (item && (item as any).payload) as WaterfallRow | undefined;
            if (!p) return '';
            return [formatCurrency(p.display, { showDecimals: false }), p.label];
          }}
          contentStyle={tooltipContentStyle}
        />
        <Bar dataKey="base" stackId="w" fill="transparent" maxBarSize={MAX_BAR_SIZE} />
        <Bar
          dataKey="value"
          stackId="w"
          radius={barRadius}
          fillOpacity={SERIES_FILL_OPACITY}
          maxBarSize={MAX_BAR_SIZE}
          {...activeBarProps}
        >
          {rows.map((r, i) => (
            <Cell key={i} fill={colorFor(r.kind, r.value)} />
          ))}
          <LabelList
            dataKey="display"
            position="top"
            formatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
            style={{ fontSize: 10, fill: theme.text }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default CashflowWaterfall;
